import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const YT_ID = "ueUXNL-A3Zg";
const YT_EMBED = `https://www.youtube.com/embed/${YT_ID}?autoplay=1&rel=0&modestbranding=1`;
const YT_THUMB = `https://i.ytimg.com/vi/${YT_ID}/maxresdefault.jpg`;

export default function VideoBlock() {
    const ref = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const inView = useInView(ref, { once: true, margin: "-15%" });

    return (
        <section
            ref={ref}
            data-testid="video-section"
            className="relative w-full py-24 sm:py-32 bg-[color:var(--paper)]"
            id="film"
        >
            <div className="max-w-[92rem] mx-auto px-6 sm:px-12 lg:px-16">
                {/* Section label */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="grid grid-cols-12 mb-10 sm:mb-16"
                >
                    <div className="col-span-12 sm:col-span-3 font-sans text-[10px] uppercase tracking-[0.35em] text-neutral-500">
                        Chapter I — The Film
                    </div>
                    <div className="col-span-12 sm:col-span-9 mt-3 sm:mt-0">
                        <h2 className="font-serif text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-none text-neutral-950">
                            <span className="italic font-light">Mistakes Were NOT Made.</span>{" "}
                            <span className="block sm:inline text-neutral-500 mt-2 sm:mt-0">
                                An anthem for justice.
                            </span>
                        </h2>
                        <p className="mt-5 max-w-2xl font-sans text-[11px] uppercase tracking-[0.3em] text-neutral-500">
                            By Margaret Anna Alice · Read by Dr. Tess Lawrie
                        </p>
                    </div>
                </motion.div>

                {/* Video frame — click to load YouTube iframe */}
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    className="relative aspect-[16/9] w-full overflow-hidden bg-neutral-950"
                >
                    {loaded ? (
                        <iframe
                            src={YT_EMBED}
                            title="Mistakes Were NOT Made: An Anthem for Justice"
                            className="absolute inset-0 w-full h-full"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                            data-testid="memorial-video-iframe"
                        />
                    ) : (
                        <button
                            type="button"
                            onClick={() => setLoaded(true)}
                            data-testid="video-play-button"
                            aria-label="Play: Mistakes Were NOT Made — An Anthem for Justice"
                            className="group absolute inset-0 w-full h-full focus:outline-none"
                        >
                            <img
                                src={YT_THUMB}
                                alt="An anthem for justice — video poster"
                                loading="lazy"
                                onError={(e) => {
                                    e.currentTarget.src = `https://i.ytimg.com/vi/${YT_ID}/hqdefault.jpg`;
                                }}
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-black/30 pointer-events-none" />
                            <span className="absolute inset-0 flex items-center justify-center">
                                <span className="relative flex items-center justify-center w-24 h-24 sm:w-32 sm:h-32 rounded-full border border-white/80 backdrop-blur-[2px] transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110">
                                    <span className="absolute inset-0 rounded-full bg-white/0 group-hover:bg-white/15 transition-colors duration-500" />
                                    <svg width="26" height="30" viewBox="0 0 26 30" fill="none" className="ml-1">
                                        <path d="M0 0 L26 15 L0 30 Z" fill="#fff" />
                                    </svg>
                                </span>
                            </span>
                            <span className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8 font-sans text-[10px] uppercase tracking-[0.35em] text-white/80">
                                Play — 2:30
                            </span>
                        </button>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
